<script lang="ts" setup>
import { inject, onMounted } from 'vue';

// COMPONENTS
import { GButton } from '@flash-global66/g-button';
import { GImage } from '@flash-global66/g-image';
import { Loader as GLoader } from '@flash-global66/g-loader';

// PROVIDERS
import transactionProvider, {
  initialValuesCashInProvider,
} from '@/modules/cashIn/config/cashInProvider';

// COMPOSABLES
import { useQueryURL } from '@/composables/useQueryURL';
import { useWompiStatus } from '@/modules/cashIn/composables/useWompiStatus';

const transactionInject = inject(
  transactionProvider,
  initialValuesCashInProvider
);

const {
  status,
  transactionNumber,
  formattedAmount,
  handleGoHome,
  handleTryAgain,
} = useWompiStatus();

const { messageSecureText } = transactionInject;

onMounted(async (): Promise<void> => {
  await useQueryURL({ cashin: 'success' });
});
</script>

<template>
  <div class="bg-gray-10 min-h-screen flex items-center justify-center relative">
    <GLoader v-if="status === 'loading'" />
    <div
      v-else-if="status === 'success'"
      class="flex flex-col gap-4 items-center p-0 w-[504px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <div class="flex items-center justify-center overflow-hidden shrink-0">
        <GImage name="check" size="xl" />
      </div>
      <div class="flex flex-col gap-4 items-start text-center w-full">
        <div class="flex font-medium gap-1 items-start justify-center text-2 text-terciary-txt w-full">
          <p>Nº de transacción</p>
          <p>{{ transactionNumber }}</p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-5 text-secondary-txt w-full">
          <p class="leading-[24.358px] whitespace-pre-wrap">
            Tu dinero ya está disponible en tu cuenta
          </p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-primary-txt text-9 font-semibold leading-[36px] w-full">
          <p class="whitespace-pre-wrap">
            Ingresaste {{ formattedAmount }}
          </p>
        </div>
      </div>
      <g-button
        class="h-[42px] w-[400px]"
        variant="primary"
        @click="handleGoHome"
        data-test="button:entendido"
      >
        Entendido
      </g-button>
    </div>
    <div
      v-else-if="status === 'error'"
      class="flex flex-col gap-4 items-center p-0 w-[504px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <div class="flex items-center justify-center overflow-hidden shrink-0">
        <GImage name="warning-sign-yellow" size="xl" />
      </div>
      <div class="flex flex-col gap-6 items-center text-center w-full whitespace-pre-wrap">
        <div class="flex flex-col justify-center shrink-0 text-primary-txt text-7 font-semibold leading-[32px] w-full">
          <p>Parece que hubo un problema</p>
        </div>
        <div class="flex flex-col justify-center shrink-0 text-secondary-txt text-4 font-medium leading-[24px] w-full">
          <p>
            No se pudo completar tu solicitud. Por favor, inténtalo de nuevo.
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-4 items-center w-full">
        <g-button
          class="h-[42px] w-[400px]"
          variant="primary"
          @click="handleTryAgain"
          data-test="button:intentar-de-nuevo"
        >
          Intentar de nuevo
        </g-button>
        <g-button
          class="h-[42px] w-[400px]"
          variant="tertiary"
          @click="handleGoHome"
          data-test="button:ir-al-inicio"
        >
          Ir al inicio
        </g-button>
      </div>
    </div>
    <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-4">
      <div class="flex justify-center items-center text-gray-21 text-2">
        <fa-icon class="mr-2" :icon="['fas', 'lock-alt']" />
        <p v-html="messageSecureText"></p>
      </div>
    </div>
  </div>
</template>
